'use client';

import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useWorkflowStore } from '../../../stores/workflowStore';
import { useUploadedFileStore } from '../../../stores/uploadedFileStore';
import { useSchema } from '../../../contexts/SchemaContext';
import { getDisplayColumns } from '../../../utils/schemaDisplay';
import { Info, Link, ArrowRight, Database, Table, CheckSquare, Square } from '@phosphor-icons/react';
import { getNodeDisplayLabel } from '../../../utils/nodeDisplayLabel';

const DATA_SOURCE_TYPES = new Set(['load_excel', 'load_csv', 'load_table', 'load_api', 'online_sheet']);

interface JoinConfigProps {
  nodeId: string;
  config: {
    leftKey?: string;
    rightKey?: string;
    joinType?: 'left' | 'inner' | 'right' | 'full';
    /** 左表要输出的列，空或未设置表示全部 */
    leftColumns?: string[];
    /** 右表要输出的列，空或未设置表示全部 */
    rightColumns?: string[];
  };
}

export function JoinConfig({ nodeId, config }: JoinConfigProps) {
  const { t, i18n } = useTranslation();
  const { nodes, edges, updateNodeConfig } = useWorkflowStore();
  const { files } = useUploadedFileStore();
  const [leftKey, setLeftKey] = useState(config.leftKey || '');
  const [rightKey, setRightKey] = useState(config.rightKey || '');
  const [joinType, setJoinType] = useState(config.joinType || 'left');

  // 当 config 变化时（如 AI 生成工作流后），同步更新内部状态
  useEffect(() => {
    if (config.leftKey) setLeftKey(config.leftKey);
    if (config.rightKey) setRightKey(config.rightKey);
    if (config.joinType) setJoinType(config.joinType);
  }, [config.leftKey, config.rightKey, config.joinType]);

  // 数据区「输入1」「输入2」的顺序：与 DataArea 一致，按 nodes 中数据源节点顺序且已有文件的
  const inputNodeIdsInTabOrder = useMemo(
    () =>
      nodes
        .filter((n) => DATA_SOURCE_TYPES.has(n.type ?? ''))
        .filter((n) => files[n.id])
        .map((n) => n.id),
    [nodes, files]
  );

  const { getNodeSchema } = useSchema();

  // 左表 = 输入1，右表 = 输入2（与数据区 Tab 顺序一致），这样下拉显示的就是用户看到的表头
  const { leftSourceId, rightSourceId, leftSourceNode, rightSourceNode } = useMemo(() => {
    const incomingSourceIds = edges.filter((e) => e.target === nodeId).map((e) => e.source);
    const firstTabId = inputNodeIdsInTabOrder[0];
    const secondTabId = inputNodeIdsInTabOrder[1];
    const leftId =
      firstTabId && incomingSourceIds.includes(firstTabId) ? firstTabId : incomingSourceIds[0];
    const rightId =
      secondTabId && incomingSourceIds.includes(secondTabId) ? secondTabId : incomingSourceIds[1];
    return {
      leftSourceId: leftId,
      rightSourceId: rightId,
      leftSourceNode: nodes.find((n) => n.id === leftId),
      rightSourceNode: nodes.find((n) => n.id === rightId),
    };
  }, [nodeId, nodes, edges, inputNodeIdsInTabOrder]);

  // 左表列：优先使用上传文件的表头（与数据区「输入1」表头一致），否则用 schema
  const leftColumns = useMemo(() => {
    if (
      leftSourceId &&
      leftSourceNode &&
      DATA_SOURCE_TYPES.has(leftSourceNode.type ?? '') &&
      files[leftSourceId]?.columns?.length
    ) {
      const headerNames = files[leftSourceId].columns;
      const nodeName = getNodeDisplayLabel(leftSourceNode.type, leftSourceNode.data?.label, t, i18n);
      return headerNames.map((name, index) => ({
        column: { name, type: 'string' as const, nullable: true, index },
        source: {
          nodeId: leftSourceId,
          nodeType: 'data_source',
          nodeName,
        },
      }));
    }
    const schema = leftSourceId && leftSourceNode ? getNodeSchema(leftSourceId) : undefined;
    const cols = schema ? getDisplayColumns(schema) : [];
    if (!cols?.length) return [];
    const nodeName = leftSourceNode
      ? getNodeDisplayLabel(leftSourceNode.type, leftSourceNode.data?.label, t, i18n)
      : '';
    return cols.map((col) => ({
      column: col,
      source: {
        nodeId: leftSourceId!,
        nodeType: leftSourceNode?.type ?? 'data_source',
        nodeName,
      },
    }));
  }, [leftSourceId, leftSourceNode, files, getNodeSchema, t, i18n]);

  // 右表列：优先使用上传文件的表头（与数据区「输入2」表头一致），否则用 schema
  const rightColumns = useMemo(() => {
    if (
      rightSourceId &&
      rightSourceNode &&
      DATA_SOURCE_TYPES.has(rightSourceNode.type ?? '') &&
      files[rightSourceId]?.columns?.length
    ) {
      const headerNames = files[rightSourceId].columns;
      const nodeName = getNodeDisplayLabel(rightSourceNode.type, rightSourceNode.data?.label, t, i18n);
      return headerNames.map((name, index) => ({
        column: { name, type: 'string' as const, nullable: true, index },
        source: {
          nodeId: rightSourceId,
          nodeType: 'data_source',
          nodeName,
        },
      }));
    }
    const schema = rightSourceId && rightSourceNode ? getNodeSchema(rightSourceId) : undefined;
    const cols = schema ? getDisplayColumns(schema) : [];
    if (!cols?.length) return [];
    const nodeName = rightSourceNode
      ? getNodeDisplayLabel(rightSourceNode.type, rightSourceNode.data?.label, t, i18n)
      : '';
    return cols.map((col) => ({
      column: col,
      source: {
        nodeId: rightSourceId!,
        nodeType: rightSourceNode?.type ?? 'data_source',
        nodeName,
      },
    }));
  }, [rightSourceId, rightSourceNode, files, getNodeSchema, t, i18n]);

  const leftColumnNames = useMemo(() => leftColumns?.map((c) => c.column.name) ?? [], [leftColumns]);
  const rightColumnNames = useMemo(() => rightColumns?.map((c) => c.column.name) ?? [], [rightColumns]);

  // 选中的左表/右表输出列：未配置或空数组表示“全部选中”
  const selectedLeftNames = useMemo(() => {
    const configured = config.leftColumns;
    if (!configured || configured.length === 0) return leftColumnNames;
    return configured.filter((n) => leftColumnNames.includes(n));
  }, [config.leftColumns, leftColumnNames]);

  const selectedRightNames = useMemo(() => {
    const configured = config.rightColumns;
    if (!configured || configured.length === 0) return rightColumnNames;
    return configured.filter((n) => rightColumnNames.includes(n));
  }, [config.rightColumns, rightColumnNames]);

  const updateConfig = (updates: {
    leftKey?: string;
    rightKey?: string;
    joinType?: string;
    leftColumns?: string[];
    rightColumns?: string[];
  }) => {
    const newConfig = {
      type: 'join',
      params: {
        leftKey: updates.leftKey ?? leftKey,
        rightKey: updates.rightKey ?? rightKey,
        joinType: updates.joinType ?? joinType,
        leftColumns: updates.leftColumns !== undefined ? updates.leftColumns : config.leftColumns,
        rightColumns: updates.rightColumns !== undefined ? updates.rightColumns : config.rightColumns,
      },
    };
    updateNodeConfig(nodeId, newConfig);
  };

  const setLeftColumnsSelection = (names: string[]) => {
    updateConfig({
      leftColumns: names.length === leftColumnNames.length ? [] : names,
    });
  };

  const setRightColumnsSelection = (names: string[]) => {
    updateConfig({
      rightColumns: names.length === rightColumnNames.length ? [] : names,
    });
  };

  const toggleLeftColumn = (name: string) => {
    const next = selectedLeftNames.includes(name)
      ? selectedLeftNames.filter((n) => n !== name)
      : [...selectedLeftNames, name];
    setLeftColumnsSelection(next);
  };

  const toggleRightColumn = (name: string) => {
    const next = selectedRightNames.includes(name)
      ? selectedRightNames.filter((n) => n !== name)
      : [...selectedRightNames, name];
    setRightColumnsSelection(next);
  };

  const joinTypes = [
    { 
      value: 'left', 
      label: t('nodes.config.join.leftJoin'), 
      sublabel: 'LEFT JOIN',
      description: t('nodes.config.join.leftJoinDesc'),
      color: 'bg-blue-50 border-blue-200 text-blue-700'
    },
    { 
      value: 'inner', 
      label: t('nodes.config.join.innerJoin'), 
      sublabel: 'INNER JOIN',
      description: t('nodes.config.join.innerJoinDesc'),
      color: 'bg-emerald-50 border-emerald-200 text-emerald-700'
    },
    { 
      value: 'right', 
      label: t('nodes.config.join.rightJoin'), 
      sublabel: 'RIGHT JOIN',
      description: t('nodes.config.join.rightJoinDesc'),
      color: 'bg-purple-50 border-purple-200 text-purple-700'
    },
    { 
      value: 'full', 
      label: t('nodes.config.join.fullJoin'), 
      sublabel: 'FULL JOIN',
      description: t('nodes.config.join.fullJoinDesc'),
      color: 'bg-amber-50 border-amber-200 text-amber-700'
    },
  ];

  const selectedType = joinTypes.find((t) => t.value === joinType);

  return (
    <div className="space-y-5">
      <div className="text-sm font-medium text-zinc-700">{t('nodes.config.join.title')}</div>
      
      {/* Join Type Selection */}
      <div className="space-y-2">
        <label className="text-xs font-medium text-zinc-500">{t('nodes.config.join.joinType')}</label>
        <div className="grid grid-cols-2 gap-2">
          {joinTypes.map((type) => (
            <motion.button
              key={type.value}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                setJoinType(type.value as typeof joinType);
                updateConfig({ joinType: type.value });
              }}
              className={`
                p-3 rounded-xl border text-left transition-all
                ${joinType === type.value
                  ? type.color
                  : 'bg-white border-zinc-200 text-zinc-600 hover:border-zinc-300'
                }
              `}
            >
              <div className="flex items-center gap-2">
                <span className="font-medium">{type.label}</span>
                <span className="text-xs opacity-70">{type.sublabel}</span>
              </div>
            </motion.button>
          ))}
        </div>
        
        {/* Selected Type Description */}
        <motion.div
          key={joinType}
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-3 bg-zinc-50 rounded-lg text-xs text-zinc-600"
        >
          {selectedType?.description}
        </motion.div>
      </div>

      {/* Join Keys：关联键选择 */}
      <div className="space-y-4">
        <label className="text-xs font-medium text-zinc-500">{t('nodes.config.join.joinKeys')}</label>

        {/* 左表关联键 */}
        <div className="space-y-1.5">
          <span className="text-xs text-zinc-400">{t('nodes.config.join.leftKey')}</span>
          <InputColumnSelector
            value={leftKey}
            onChange={(columnName) => {
              setLeftKey(columnName);
              updateConfig({ leftKey: columnName });
            }}
            columns={leftColumns ?? []}
            placeholder={t('nodes.config.join.selectLeft')}
            label={t('nodes.config.join.leftKey')}
          />
        </div>

        {/* 连接示意 */}
        <div className="flex items-center justify-center py-0.5">
          <div className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center">
            <Link className="w-4 h-4 text-zinc-400" />
          </div>
        </div>

        {/* 右表关联键 */}
        <div className="space-y-1.5">
          <span className="text-xs text-zinc-400">{t('nodes.config.join.rightKey')}</span>
          <InputColumnSelector
            value={rightKey}
            onChange={(columnName) => {
              setRightKey(columnName);
              updateConfig({ rightKey: columnName });
            }}
            columns={rightColumns ?? []}
            placeholder={t('nodes.config.join.selectRight')}
            label={t('nodes.config.join.rightKey')}
          />
        </div>

        {/* Auto-match suggestion */}
        {leftKey && !rightKey && rightColumns?.some(col => col.column.name === leftKey) && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={() => {
              setRightKey(leftKey);
              updateConfig({ rightKey: leftKey });
            }}
            className="flex items-center gap-1.5 text-xs text-emerald-600 hover:text-emerald-700"
          >
            <ArrowRight className="w-3.5 h-3.5" />
            {t('nodes.config.join.autoMatch')}
          </motion.button>
        )}
      </div>

      {/* 输出字段：左表/右表各选要保留的列，默认全部选中 */}
      <div className="space-y-4">
        <label className="text-xs font-medium text-zinc-500">{t('nodes.config.join.outputFields')}</label>
        <p className="text-[11px] text-zinc-400">{t('nodes.config.join.outputFieldsHint')}</p>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* 左表输出字段 */}
          <div className="space-y-2 rounded-xl border border-zinc-200 bg-zinc-50/50 p-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-zinc-600">{t('nodes.config.join.leftOutputFields')}</span>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => setLeftColumnsSelection(leftColumnNames)}
                  className="text-[10px] text-emerald-600 hover:text-emerald-700"
                >
                  {t('nodes.config.join.selectAll')}
                </button>
                <span className="text-zinc-300">|</span>
                <button
                  type="button"
                  onClick={() => setLeftColumnsSelection([])}
                  className="text-[10px] text-zinc-500 hover:text-zinc-700"
                >
                  {t('nodes.config.join.deselectAll')}
                </button>
              </div>
            </div>
            <div className="max-h-40 overflow-y-auto space-y-1">
              {leftColumnNames.length === 0 ? (
                <p className="text-[11px] text-zinc-400">{t('nodes.config.join.connectUpstream')}</p>
              ) : (
                leftColumnNames.map((name) => (
                  <label
                    key={name}
                    className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-white/80 cursor-pointer"
                  >
                    <span className="flex-shrink-0">
                      {selectedLeftNames.includes(name) ? (
                        <CheckSquare className="w-4 h-4 text-emerald-600" weight="fill" />
                      ) : (
                        <Square className="w-4 h-4 text-zinc-400" />
                      )}
                    </span>
                    <span className="text-xs text-zinc-700 truncate">{name}</span>
                    <input
                      type="checkbox"
                      checked={selectedLeftNames.includes(name)}
                      onChange={() => toggleLeftColumn(name)}
                      className="sr-only"
                    />
                  </label>
                ))
              )}
            </div>
            {leftColumnNames.length > 0 && (
              <p className="text-[10px] text-zinc-400">
                {t('nodes.config.join.selectedCount', { count: selectedLeftNames.length, total: leftColumnNames.length })}
              </p>
            )}
          </div>

          {/* 右表输出字段 */}
          <div className="space-y-2 rounded-xl border border-zinc-200 bg-zinc-50/50 p-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-zinc-600">{t('nodes.config.join.rightOutputFields')}</span>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => setRightColumnsSelection(rightColumnNames)}
                  className="text-[10px] text-emerald-600 hover:text-emerald-700"
                >
                  {t('nodes.config.join.selectAll')}
                </button>
                <span className="text-zinc-300">|</span>
                <button
                  type="button"
                  onClick={() => setRightColumnsSelection([])}
                  className="text-[10px] text-zinc-500 hover:text-zinc-700"
                >
                  {t('nodes.config.join.deselectAll')}
                </button>
              </div>
            </div>
            <div className="max-h-40 overflow-y-auto space-y-1">
              {rightColumnNames.length === 0 ? (
                <p className="text-[11px] text-zinc-400">{t('nodes.config.join.connectUpstream')}</p>
              ) : (
                rightColumnNames.map((name) => (
                  <label
                    key={name}
                    className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-white/80 cursor-pointer"
                  >
                    <span className="flex-shrink-0">
                      {selectedRightNames.includes(name) ? (
                        <CheckSquare className="w-4 h-4 text-emerald-600" weight="fill" />
                      ) : (
                        <Square className="w-4 h-4 text-zinc-400" />
                      )}
                    </span>
                    <span className="text-xs text-zinc-700 truncate">{name}</span>
                    <input
                      type="checkbox"
                      checked={selectedRightNames.includes(name)}
                      onChange={() => toggleRightColumn(name)}
                      className="sr-only"
                    />
                  </label>
                ))
              )}
            </div>
            {rightColumnNames.length > 0 && (
              <p className="text-[10px] text-zinc-400">
                {t('nodes.config.join.selectedCount', { count: selectedRightNames.length, total: rightColumnNames.length })}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Info Tip */}
      <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-xl border border-blue-100">
        <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-blue-700 leading-relaxed">
          {t('nodes.config.join.joinTip')}
        </p>
      </div>
    </div>
  );
}

// 输入列选择器组件
interface ColumnSelectorProps {
  value: string;
  onChange: (columnName: string) => void;
  columns: { column: { name: string; type: string }; source: { nodeId: string; nodeType: string; nodeName: string } }[] | undefined;
  placeholder?: string;
  label: string;
}

function InputColumnSelector({ value, onChange, columns, placeholder }: ColumnSelectorProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  if (!columns || columns.length === 0) {
    return (
      <div className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-sm text-zinc-400">
        {t('nodes.config.join.connectUpstream')}
      </div>
    );
  }

  const selectedColumn = columns.find(({ column }) => column.name === value);
  const sourceName = columns[0]?.source.nodeName || t('nodes.config.join.unknownSource');
  const isDataSource = columns[0]?.source.nodeType === 'data_source';

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`
          w-full flex items-center justify-between gap-2 px-3 py-2 
          bg-white border rounded-lg text-sm text-left
          transition-all duration-200
          ${isOpen 
            ? 'border-emerald-500 ring-2 ring-emerald-500/20' 
            : 'border-zinc-200 hover:border-zinc-300'
          }
        `}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
          {selectedColumn ? (
            <>
              <span className="font-medium text-zinc-900 break-words">
                {selectedColumn.column.name}
              </span>
              <span className="text-xs text-zinc-400 flex-shrink-0">
                ({selectedColumn.column.type})
              </span>
            </>
          ) : (
            <span className="text-zinc-400">{placeholder}</span>
          )}
        </div>
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown Panel：全宽、最小宽度，列表项完整显示字段名 */}
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            className="absolute top-full left-0 right-0 mt-2 z-50 min-w-[240px] bg-white rounded-xl border border-zinc-200 shadow-xl shadow-zinc-900/10 overflow-hidden"
          >
            {/* Source Header */}
            <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-100 flex items-center gap-2">
              {isDataSource ? (
                <Database className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
              ) : (
                <Table className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
              )}
              <span className="text-xs font-medium text-zinc-600 break-words min-w-0">
                {sourceName}
              </span>
            </div>

            {/* Column List：字段名完整显示、可换行 */}
            <div className="max-h-56 overflow-y-auto p-1">
              {columns.map(({ column }) => (
                <button
                  key={column.name}
                  type="button"
                  onClick={() => {
                    onChange(column.name);
                    setIsOpen(false);
                  }}
                  className={`
                    w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-sm text-left
                    transition-colors
                    ${value === column.name
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'hover:bg-zinc-50 text-zinc-700'
                    }
                  `}
                >
                  <span className="font-medium break-words min-w-0 flex-1">
                    {column.name}
                  </span>
                  <span className="text-xs text-zinc-400 flex-shrink-0">
                    ({column.type})
                  </span>
                </button>
              ))}
            </div>

            {/* Footer */}
            <div className="px-3 py-2 bg-zinc-50 border-t border-zinc-100 text-xs text-zinc-400">
              {t('nodes.config.join.totalColumns', { count: columns.length })}
            </div>
          </motion.div>
        </>
      )}
    </div>
  );
}
